import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { collectionGroup, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { db, getAuthClient } from "../firebaseConfig";
import { readOnTimeSession, readLinkProfileSession } from "../ontime/auth/session";
import { normalizeAppName, normalizeApps } from "./appAccess";

export const AuthCtx = createContext(null);

function uniqStrings(values = []) {
  return [...new Set((values || []).filter(Boolean).map((v) => String(v)))];
}

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function readPreferredOrgId() {
  if (typeof window === "undefined") return "";
  try {
    return String(
      window.sessionStorage.getItem("im.activeOrgId") ||
      window.localStorage.getItem("im.activeOrgId") ||
      ""
    ).trim();
  } catch (_) {
    return "";
  }
}

function persistPreferredOrgId(orgId) {
  const value = String(orgId || "").trim();
  if (!value || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem("im.activeOrgId", value);
    window.localStorage.setItem("im.activeOrgId", value);
  } catch (_) {}
}

function normalizeDocApps(source = {}) {
  return normalizeApps(source).map((app) => normalizeAppName(app)).filter(Boolean);
}

function normalizeExplicitMemberApps(memberDoc = null) {
  if (!memberDoc || typeof memberDoc !== "object") return [];

  const values = [];
  const collect = (raw) => {
    normalizeApps(raw).forEach((app) => {
      const normalized = normalizeAppName(app);
      if (normalized) values.push(normalized);
    });
  };

  // Super Admin member toggles are the source of truth. Only read explicit
  // access fields here. Do not infer Neo from legacy defaultApp/role/owner fields.
  collect(memberDoc.apps);
  collect(memberDoc.enabledApps);
  collect(memberDoc.appAccess);
  collect(memberDoc.appsMap);
  collect(memberDoc.access);
  collect(memberDoc.permissions);

  return uniqStrings(values);
}

function mergeUserAccess(baseClaims = {}, roleDoc = null, managerDoc = null, memberDoc = null) {
  const baseApps = normalizeDocApps(baseClaims);
  const roleApps = normalizeDocApps(roleDoc || {});
  const managerApps = normalizeDocApps(managerDoc || {});
  const explicitMemberApps = normalizeExplicitMemberApps(memberDoc);
  const memberExists = !!memberDoc && typeof memberDoc === "object";

  // If a Super Admin member document exists and has app toggles, use it as the
  // final layer access list. This prevents old custom claims/userRoles/managers
  // from leaking users into Neo after they were changed to MC-only.
  // The Super Admin member document is the source of truth for per-user app
  // access. If the member exists and On Time/Link is toggled off, do not fall
  // back to org-level/default app or legacy userRoles/managers claims.
  const sourceApps = memberExists
    ? explicitMemberApps
    : [
        ...baseApps,
        ...roleApps,
        ...managerApps,
        normalizeAppName(baseClaims?.defaultApp),
        normalizeAppName(roleDoc?.defaultApp),
        normalizeAppName(managerDoc?.defaultApp),
      ];

  const apps = uniqStrings(sourceApps).filter(Boolean);

  return {
    ...baseClaims,
    ...(roleDoc || {}),
    ...(managerDoc || {}),
    ...(memberDoc || {}),
    apps,
    defaultApp: apps[0] || "",
  };
}

async function findMemberByEmail(email, preferredOrgId = "", uid = "") {
  const cleanEmail = String(email || "").trim().toLowerCase();
  const cleanPreferredOrgId = String(preferredOrgId || "").trim();
  const cleanUid = String(uid || "").trim();
  if (!cleanEmail) return null;

  // Login resolves the intended organization before routing. Read that exact
  // organization's member document first so duplicate emails in older orgs
  // cannot hydrate the wrong MatrixOS access profile.
  if (cleanPreferredOrgId) {
    const directIds = [...new Set([cleanUid, cleanEmail].filter(Boolean))];
    for (const memberId of directIds) {
      try {
        const snap = await getDoc(doc(db, "orgs", cleanPreferredOrgId, "members", memberId));
        if (snap.exists()) {
          return { orgId: cleanPreferredOrgId, data: snap.data() || {} };
        }
      } catch (_) {}
    }
  }

  const attempts = [
    ["email", cleanEmail],
    ["emailLower", cleanEmail],
  ];

  // When switching orgs, the token/custom claims may already know the intended
  // org. Prefer that org's member document first. A collectionGroup limit(1)
  // can return the same email from an older org and make MC hydrate the wrong
  // Morpheus data.
  if (cleanPreferredOrgId) {
    for (const [field, value] of attempts) {
      try {
        const snap = await getDocs(query(collectionGroup(db, "members"), where(field, "==", value), limit(25)));
        const preferred = snap.docs.find((memberDoc) => String(memberDoc.ref.parent.parent?.id || "") === cleanPreferredOrgId);
        if (preferred) {
          return {
            orgId: preferred.ref.parent.parent?.id || "",
            data: preferred.data() || {},
          };
        }
      } catch (_) {}
    }
  }

  for (const [field, value] of attempts) {
    try {
      const snap = await getDocs(query(collectionGroup(db, "members"), where(field, "==", value), limit(1)));
      if (!snap.empty) {
        const memberDoc = snap.docs[0];
        return {
          orgId: memberDoc.ref.parent.parent?.id || "",
          data: memberDoc.data() || {},
        };
      }
    } catch (_) {}
  }

  return null;
}

async function buildClaimsFromTokenAndDocs(user, baseClaims = {}) {
  const uid = String(user?.uid || "").trim();
  const email = String(user?.email || "").trim().toLowerCase();

  let orgId = String(baseClaims?.orgId || readPreferredOrgId() || "").trim();
  let role = normalizeRole(baseClaims?.role);
  let roleDoc = null;
  let managerDoc = null;
  let memberDoc = null;

  const memberRef = await findMemberByEmail(email, orgId, uid);
  if (memberRef?.data) {
    memberDoc = memberRef.data;
    if (memberRef.orgId) orgId = memberRef.orgId;
    persistPreferredOrgId(orgId);

    // Phase 8 authority hardening: once a current-organisation member record is
    // resolved, that record is authoritative for the organisation role. Do not
    // let an older custom claim/userRoles record (for example a former Owner in
    // another organisation) override the current organisation's membership.
    if (memberDoc?.role) role = normalizeRole(memberDoc.role);
  }

  if (uid) {
    try {
      const snap = await getDoc(doc(db, "userRoles", uid));
      if (snap.exists()) {
        roleDoc = snap.data() || {};
        if (!orgId && roleDoc?.orgId) orgId = String(roleDoc.orgId);
        if (!memberDoc && !role && roleDoc?.role) role = normalizeRole(roleDoc.role);
      }
    } catch (_) {}
  }

  if (uid) {
    try {
      const snap = await getDoc(doc(db, "managers", uid));
      if (snap.exists()) {
        managerDoc = snap.data() || {};
        if (!orgId && managerDoc?.orgId) orgId = String(managerDoc.orgId);
        if (!memberDoc && !role && managerDoc?.role) role = normalizeRole(managerDoc.role);
      }
    } catch (_) {}
  }

  let nextClaims = mergeUserAccess(baseClaims, roleDoc, managerDoc, memberDoc);

  let orgApps = [];
  if (orgId) {
    try {
      const orgSnap = await getDoc(doc(db, "orgs", orgId));
      if (orgSnap.exists()) {
        const orgData = orgSnap.data() || {};
        orgApps = normalizeDocApps(orgData);
      }
    } catch (_) {}
  }

  const isSuper = nextClaims?.superAdmin === true || nextClaims?.superadmin === true;
  if (!isSuper && orgApps.length) {
    const allowedOrgApps = new Set(orgApps);
    nextClaims.apps = normalizeDocApps(nextClaims).filter((app) => allowedOrgApps.has(app));
    if (nextClaims.defaultApp && !allowedOrgApps.has(normalizeAppName(nextClaims.defaultApp))) {
      nextClaims.defaultApp = nextClaims.apps[0] || "";
    }
  }

  nextClaims = {
    ...nextClaims,
    orgId: orgId || nextClaims.orgId || "",
    orgApps,
    role: role || normalizeRole(nextClaims.role) || "",
    email: email || nextClaims.email || "",
    uid: uid || nextClaims.uid || "",
  };

  return nextClaims;
}

function preserveAuthoritativeAccess(authoritativeClaims = {}, sessionClaims = {}) {
  // Link profile sessions are allowed to add profile/display fields, but they
  // must never grant app access. App access always comes from the current
  // org/member document loaded in buildClaimsFromTokenAndDocs().
  const apps = Array.isArray(authoritativeClaims?.apps) ? authoritativeClaims.apps : [];
  return {
    ...authoritativeClaims,
    ...(sessionClaims || {}),
    orgId: authoritativeClaims?.orgId || sessionClaims?.orgId || "",
    orgApps: authoritativeClaims?.orgApps || [],
    apps,
    defaultApp: apps[0] || "",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshClaims = async ({ force = true } = {}) => {
    const auth = getAuthClient();
    const u = auth.currentUser;
    if (!u) {
      setUser(null);
      setClaims(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    try {
      if (force) await u.getIdToken(true);
      const tokenRes = await getIdTokenResult(u, force);
      const baseClaims = tokenRes?.claims || {};
      let nextClaims = await buildClaimsFromTokenAndDocs(u, baseClaims);
      const onTimeSession = readOnTimeSession();
      const sessionMatches =
        onTimeSession?.app === "ontime" &&
        String(onTimeSession?.uid || "").trim() === String(u?.uid || "").trim() &&
        String(onTimeSession?.email || "").trim().toLowerCase() === String(u?.email || "").trim().toLowerCase();
      if (sessionMatches && onTimeSession?.claims) nextClaims = preserveAuthoritativeAccess(nextClaims, onTimeSession.claims);
      const linkProfileSession = readLinkProfileSession();
      const linkProfileMatches =
        linkProfileSession?.app === "ontime" &&
        String(linkProfileSession?.uid || "").trim() === String(u?.uid || "").trim() &&
        String(linkProfileSession?.email || "").trim().toLowerCase() === String(u?.email || "").trim().toLowerCase() &&
        (!linkProfileSession?.orgId || String(linkProfileSession.orgId) === String(nextClaims?.orgId || ""));
      if (linkProfileMatches && linkProfileSession?.claims) nextClaims = preserveAuthoritativeAccess(nextClaims, linkProfileSession.claims);
      setUser(u);
      setClaims(nextClaims);
      return nextClaims;
    } catch (err) {
      console.error("[AuthProvider] refreshClaims failed:", err);
      setUser(u);
      setClaims(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const auth = getAuthClient();

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setClaims(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const tokenRes = await getIdTokenResult(u, false);
        const baseClaims = tokenRes?.claims || {};
        let nextClaims = await buildClaimsFromTokenAndDocs(u, baseClaims);
        const onTimeSession = readOnTimeSession();
        const sessionMatches =
          onTimeSession?.app === "ontime" &&
          String(onTimeSession?.uid || "").trim() === String(u?.uid || "").trim() &&
          String(onTimeSession?.email || "").trim().toLowerCase() === String(u?.email || "").trim().toLowerCase();
        if (sessionMatches && onTimeSession?.claims) nextClaims = preserveAuthoritativeAccess(nextClaims, onTimeSession.claims);
      const linkProfileSession = readLinkProfileSession();
      const linkProfileMatches =
        linkProfileSession?.app === "ontime" &&
        String(linkProfileSession?.uid || "").trim() === String(u?.uid || "").trim() &&
        String(linkProfileSession?.email || "").trim().toLowerCase() === String(u?.email || "").trim().toLowerCase() &&
        (!linkProfileSession?.orgId || String(linkProfileSession.orgId) === String(nextClaims?.orgId || ""));
      if (linkProfileMatches && linkProfileSession?.claims) nextClaims = preserveAuthoritativeAccess(nextClaims, linkProfileSession.claims);
        setUser(u);
        setClaims(nextClaims);
      } catch (err) {
        console.error("[AuthProvider] token fetch failed:", err);
        setUser(u);
        setClaims(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const value = useMemo(
    () => ({ user, claims, loading, refreshClaims }),
    [user, claims, loading]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
